import { useRef, useEffect, useMemo } from 'react';
import type { MediaItem, MediaType } from '../types/movie';
import { MovieCard } from './MovieCard';
import { usePrefetchRatingsBatch } from '../hooks/usePrefetchRatingsBatch';

interface MovieGridProps {
  movies: MediaItem[];
  loading: boolean;
  isFetchingNextPage?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onMovieClick?: (movie: MediaItem) => void;
  mediaType?: MediaType;
}

export function MovieGrid({
  movies,
  loading,
  isFetchingNextPage = false,
  hasMore,
  onLoadMore,
  onMovieClick,
  mediaType = 'movie',
}: MovieGridProps) {
  const isTV = mediaType === 'tv';
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Batch prefetch ratings for visible items (prevents per-card waterfall)
  const ratingItems = useMemo(
    () => movies.slice(0, 100).map((m) => ({ media_id: m.id, media_type: mediaType })),
    [movies, mediaType]
  );
  usePrefetchRatingsBatch(ratingItems);

  // Infinite scroll using IntersectionObserver
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, loading, onLoadMore]);

  if (!loading && movies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <div className="text-6xl mb-4">{isTV ? '📺' : '🎬'}</div>
        <h3 className="text-xl font-semibold mb-2">No {isTV ? 'TV shows' : 'movies'} found</h3>
        <p className="text-sm text-slate-500">
          Try adjusting your filters to find more results
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
        {movies.map((movie) => (
          <MovieCard key={movie.id} movie={movie} onClick={onMovieClick} />
        ))}

        {/* Loading skeletons - show during initial load OR pagination */}
        {(loading || isFetchingNextPage) &&
          Array.from({ length: 14 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="group">
              <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1F26]">
                <div className="w-full h-full skeleton" />
              </div>
              <div className="mt-2 px-1 space-y-1">
                <div className="h-4 skeleton rounded w-3/4" />
              </div>
            </div>
          ))}
      </div>

      {/* Infinite scroll trigger */}
      <div ref={loadMoreRef} className="h-1" />
    </div>
  );
}
